import { useEffect, useState } from 'react';
import { CustomNumberInput } from '../common/CustomNumberInput';
import { CustomSelect } from '../common/CustomSelect';
import { childAgeOptions } from '../common/utils/filters';

export interface PaxCombinationProps {
    numberOfAdults?: number;
    agesOfChildren?: number[];
    updatePax: (numberOfAdults: number, agesOfChildren: number[]) => void;
    className?: string;
}

const MAX_PAX = 4;
const MAX_CHILDREN = 2;

export const PaxCombination = ({ numberOfAdults, agesOfChildren, updatePax, className }: PaxCombinationProps) => {
    const [adults, setAdults] = useState(numberOfAdults ?? 2);
    const [agesChildren, setAgesChildren] = useState(agesOfChildren ?? []);

    const updateAdults = (newAdults: number) => {
        setAdults(newAdults);
        updatePax(newAdults, agesChildren);
    };

    const setChildrenCount = (newCount: number) => {
        const adjustedChildren =
            newCount > agesChildren.length
                ? [...agesChildren, ...new Array(newCount - agesChildren.length).fill(0)]
                : agesChildren.slice(0, newCount);
        setAgesChildren(adjustedChildren);
        updatePax(adults, adjustedChildren);
    };

    const setChildAge = (childIndex: number, age: number) => {
        const updatedChildrensAges = [...agesChildren];
        updatedChildrensAges[childIndex] = age;
        setAgesChildren(updatedChildrensAges);
        updatePax(adults, updatedChildrensAges);
    };

    useEffect(() => {
        setAdults(numberOfAdults ?? 2);
        setAgesChildren(agesOfChildren ?? []);
    }, [numberOfAdults, agesOfChildren]);

    const isMaxAdults = adults + agesChildren.length >= MAX_PAX;
    const isMaxChildren = agesChildren.length >= MAX_CHILDREN || adults + agesChildren.length >= MAX_PAX;

    return (
        <fieldset className={className ?? 'flex flex-col gap-12'}>
            <legend className="sr-only">Velg antall reisende og barnas alder</legend>
            <CustomNumberInput
                id="number-of-adults-input"
                label="Voksne"
                value={adults}
                setNumber={updateAdults}
                min={1}
                max={4}
                disableSubtract={adults <= 1}
                disableAdd={isMaxAdults}
            />
            <CustomNumberInput
                id="age-of-children-input"
                label="Barn"
                value={agesChildren.length}
                setNumber={setChildrenCount}
                min={0}
                max={2}
                disableSubtract={agesChildren.length <= 0}
                disableAdd={isMaxChildren}
            />
            {agesChildren.map((childAge, childIndex) => (
                <CustomSelect
                    key={`age-of-children-select-${childIndex}-${childAge}`}
                    value={childAge.toString()}
                    onChange={(val) => setChildAge(childIndex, Number.parseInt(val))}
                    options={childAgeOptions}
                    triggerLabel={`Alder barn ${childIndex + 1}`}
                    fullscreenTitle={`Alder barn ${childIndex + 1}`}
                    hideLabelOnSmallScreen={false}
                    relative
                />
            ))}
        </fieldset>
    );
};
